import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

export type TenantLocationType = {
  id: string;
  country: string;
  state: string | null;
  city: string;
  currency: string | null;
  airportCode?: string | null;
  airportName?: string | null;
  timeZone?: string | null;
  reviewStatus: "unreviewed" | "reviewed";
  createdAt: string;
  updatedAt: string;
};

export type TenantLocationPayloadType = {
  country: string;
  state?: string | null;
  city: string;
  currency?: string | null;
  airportCode?: string | null;
  airportName?: string | null;
  timeZone?: string | null;
};

export type FlightLocation = Pick<TenantLocationType, "id" | "city" | "country" | "airportCode" | "airportName" | "timeZone">;

/** Fetches searchable flight locations from the authenticated tenant's visible catalogue. */
export const getFlightLocations = async (search = ""): Promise<FlightLocation[]> =>
  (await axios.get<FlightLocation[]>("/api/locations/flight-options", { params: { search } })).data;

/** Provides city and airport-code suggestions for flight autocomplete. */
export const useFlightLocationsQuery = (search = "") => useQuery({
  queryKey: ["flight-locations", search],
  queryFn: () => getFlightLocations(search),
  staleTime: 60_000,
});

/** Lists active custom locations belonging to the authenticated tenant. */
export const useTenantLocationsQuery = (search = "") => useQuery({
  queryKey: ["tenant-locations", search],
  queryFn: async (): Promise<TenantLocationType[]> => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    const query = params.toString();
    const response = await axios.get(`/api/locations${query ? `?${query}` : ""}`);
    return response.data?.data || [];
  },
});

/** Creates a tenant location, reusing a matching global row when one exists. */
export const useCreateTenantLocationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TenantLocationPayloadType) => (await axios.post("/api/locations", payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-locations"] });
      queryClient.invalidateQueries({ queryKey: ["city-locations"] });
      queryClient.invalidateQueries({ queryKey: ["cities"] });
      queryClient.invalidateQueries({ queryKey: ["country"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};

/** Updates a custom location owned by the authenticated tenant. */
export const useUpdateTenantLocationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: TenantLocationPayloadType & { id: string }) =>
      (await axios.put(`/api/locations/${id}`, payload)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-locations"] });
      queryClient.invalidateQueries({ queryKey: ["city-locations"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};

/** Archives a custom location owned by the authenticated tenant. */
export const useArchiveTenantLocationMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await axios.delete(`/api/locations/${id}`)).data as { success: boolean },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tenant-locations"] });
      queryClient.invalidateQueries({ queryKey: ["city-locations"] });
      queryClient.invalidateQueries({ queryKey: ["flight-locations"] });
      queryClient.invalidateQueries({ queryKey: ["timezone-values"] });
    },
  });
};
